import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { SessionItem, UpcomingSessionData } from '../../../../interaxDesignSystemComponents'
import { ActivityWithSprint, getActivityTypeName } from './helpers'
import { ActivityType } from '../../../../../../pure-js/enums/ActivityType'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { UpcomingSessionParticipantItem } from './UpcomingSessionParticipantItem'
import { downloadIntroductionMaterial } from '../../../../shared/utils/pdfDownload.helper'
import { loadCloudFunctionsHandler } from '../../../../apiLayer/handlers'
import useAppState from '../../../../hooks/useAppStateSession'
import toast from 'react-hot-toast'
import { EnvelopeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { useTeamsByIds } from '../../../../apiLayer/queryHooks/queries'
import { buildSessionDisplayTitle } from '../../utils/sessionTitle'

interface SessionListItemProps {
  activity: ActivityWithSprint
  expandable?: boolean
}

/**
 * Session List Item using the "upcoming" type with action button and optional accordion
 */
export const SessionListItem: React.FC<SessionListItemProps> = ({ activity, expandable = true }) => {
  const { t } = useTranslation()
  const { state } = useAppState()
  const [isSendingFeedback, setIsSendingFeedback] = useState(false)
  const [showAllParticipants, setShowAllParticipants] = useState(false)
  const startDate = new Date(activity.startDate)

  // Fetch team data for the activity
  const { data: teamsResponse } = useTeamsByIds(activity.teamIds || [])
  const teams = teamsResponse?.data || []

  // Determine button text and action based on activity type
  const getButtonConfig = () => {
    switch (activity.type) {
      case ActivityType.INTRODUCTION:
        return {
          text: t('facilitator.sessions.actions.downloadIntroductionMaterial'),
          action: async () => {
            try {
              await downloadIntroductionMaterial()
              toast.success(t('facilitator.sessions.messages.downloadSuccess'))
            } catch (error) {
              console.error('Failed to download introduction material:', error)
              toast.error(t('facilitator.sessions.messages.downloadError'))
            }
          },
          icon: ArrowDownTrayIcon
        }
      case ActivityType.FIRST_GAME: {
        // Check if it's the first gaming session (Gaming Session 1)

        return {
          text: t('facilitator.sessions.actions.checkOnboardingStatus'),
          action: () => {}
        }
      }

      case ActivityType.SHORT_GAME:
      case ActivityType.LONG_GAME: {
        // Check if it's the first gaming session (Gaming Session 1)
        const isFirstGamingSession =
          activity.title?.toLowerCase().includes('gaming session 1') ||
          activity.title?.toLowerCase().includes('game session 1') ||
          activity.title?.toLowerCase().includes('1st game')

        if (isFirstGamingSession) {
          return {
            text: t('facilitator.sessions.actions.checkOnboardingStatus'),
            action: () => {}
          }
        } else {
          // No button for other gaming sessions
          return null
        }
      }
      case ActivityType.SHORT_REFLECTION:
      case ActivityType.LONG_REFLECTION:
        return {
          text: t('facilitator.sessions.actions.sendFeedbackReminder'),
          action: async () => {
            if (isSendingFeedback) return

            setIsSendingFeedback(true)

            try {
              const cloudFunctionsHandler = await loadCloudFunctionsHandler()
              const result = await cloudFunctionsHandler.sendSprintFeedbackReminder(
                activity.sprintId,
                activity.id,
                state as any
              )

              if (result.success) {
                toast.success(t('facilitator.sessions.messages.feedbackReminderSuccess'))
              } else {
                console.error('❌ Failed to send feedback reminder:', result.error)
                toast.error(result.error || t('facilitator.sessions.messages.feedbackReminderError'))
              }
            } catch (error) {
              console.error('❌ Error sending feedback reminder:', error)
              toast.error(t('facilitator.sessions.messages.feedbackReminderError'))
            } finally {
              setIsSendingFeedback(false)
            }
          },
          icon: EnvelopeIcon
        }
      default:
        return {
          text: t('facilitator.sessions.actions.viewDetails'),
          action: () => {}
        }
    }
  }

  const buttonConfig = getButtonConfig()

  // Format time from startDate
  const time = startDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })

  // Determine status (live if activity is currently running)
  const now = new Date()
  const endDate = activity.endDate ? new Date(activity.endDate) : new Date(startDate.getTime() + 2 * 60 * 60 * 1000)
  const status = startDate <= now && now <= endDate ? 'live' : 'upcoming'

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 0.5
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          color: INTERAX_COLORS.white,
          fontWeight: 600,
          fontSize: '16px',
          lineHeight: '20px'
        }}
      >
        {`${label}: `}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: INTERAX_COLORS.thunderGrey,
          fontSize: '14px',
          lineHeight: '16px'
        }}
      >
        {value}
      </Typography>
    </Box>
  )

  // Build details content for accordion
  const details = expandable ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
      }}
    >
      <DetailRow label={t('facilitator.sessions.details.sprint')} value={activity.sprintTitle} />
      <DetailRow
        label={t('facilitator.sessions.details.activityType')}
        value={getActivityTypeName(activity.type as ActivityType, t as (key: string, defaultValue?: string) => string)}
      />
      {activity.description && (
        <DetailRow label={t('facilitator.sessions.details.description')} value={activity.description} />
      )}

      {/* Participants Section */}
      {activity.participants && activity.participants.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: INTERAX_COLORS.white,
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '28px'
            }}
          >
            {t('facilitator.sessions.details.participants')}
          </Typography>
          {(showAllParticipants ? activity.participants : activity.participants.slice(0, 4)).map(
            (participant, index) => (
              <UpcomingSessionParticipantItem
                key={participant.userId || index}
                participant={participant}
                teams={teams.map((team) => ({
                  name: team.name || '',
                  teamId: team.id,
                  tempTeamId: team.id
                }))}
                sprintId={activity.sprintId}
                activityId={activity.id}
                emailsSent={activity.emailsSent}
              />
            )
          )}
          {activity.participants.length > 4 && (
            <Typography
              variant="body2"
              sx={{
                color: INTERAX_COLORS.sun,
                cursor: 'pointer',
                textDecoration: 'underline',
                mt: 1
              }}
              onClick={() => setShowAllParticipants((prev) => !prev)}
            >
              {showAllParticipants
                ? t('facilitator.sessions.details.showLess')
                : t('facilitator.sessions.details.showMore', { count: activity.participants.length - 4 })}
            </Typography>
          )}
        </Box>
      )}

      <DetailRow label={t('facilitator.sessions.details.teams')} value={activity.teamIds?.length || 0} />
    </Box>
  ) : undefined

  // Convert ActivityWithSprint to UpcomingSessionData format
  const teamDisplayName = teams
    .map((team) => team.name)
    .filter((name): name is string => Boolean(name && name.trim()))
    .join(', ')

  const sessionTitle = buildSessionDisplayTitle({
    activityTitle: activity.title,
    sessionName: activity.description,
    teamName: teamDisplayName || activity.teamIds?.[0],
    fallback: activity.title || activity.id
  })

  const upcomingData: UpcomingSessionData = {
    day: startDate.getDate(),
    month: startDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
    title: sessionTitle,
    sprintName: activity.sprintTitle,
    teams: activity.teamIds?.length || 1,
    participants: activity.participants?.length || 0,
    time,
    status,
    buttonText: buttonConfig
      ? isSendingFeedback && buttonConfig.icon
        ? t('facilitator.sessions.actions.sending')
        : buttonConfig.text
      : '',
    onButtonClick: buttonConfig ? buttonConfig.action : () => {},
    buttonIcon: buttonConfig?.icon ? <buttonConfig.icon className="w-4 h-4" /> : undefined,
    expandable, // Keep expandable even if no button
    details // Always include details for accordion
  }

  return <SessionItem type="upcoming" upcomingData={upcomingData} />
}
